import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Copy, Share2, Users, Gift, TrendingUp, Award } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from './ui/skeleton';
import GenericShareModal from './GenericShareModal';

export const ReferralCodeCard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  const { data: referralData, isLoading } = useQuery({
    queryKey: ['referral-stats', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_referral_stats');
      if (error) throw error;
      return data as any;
    },
    enabled: !!user?.id
  });
  
  const referralCode = referralData?.stats?.referral_code as string | undefined;
  const stats = referralData?.stats as any;
  const recentReferrals = (referralData?.recent_referrals || []) as any[];
  
  const handleCopy = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success(t('referrals.codeCopied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleShare = () => {
    setIsShareModalOpen(true);
  };
  
  const shareText = t('referrals.shareText', { code: referralCode });
  const shareUrl = `${window.location.origin}/auth?signup=true&ref=${referralCode}`;
  
  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  
  if (!referralCode) return null;
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            {t('referrals.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">{t('referrals.yourCode')}:</p>
            <div className="flex gap-2">
              <Input 
                value={referralCode} 
                readOnly 
                className="font-mono font-bold text-lg"
              />
              <Button onClick={handleCopy} variant="outline" size="icon">
                <Copy className={`h-4 w-4 ${copied ? 'text-green-500' : ''}`} />
              </Button>
              <Button onClick={handleShare} variant="outline" size="icon">
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Users className="h-4 w-4 text-primary mr-1" />
              </div>
              <p className="text-2xl font-bold text-primary">{stats?.total_referrals || 0}</p>
              <p className="text-xs text-muted-foreground">{t('referrals.totalReferrals')}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
              </div>
              <p className="text-2xl font-bold text-green-600">{stats?.completed_referrals || 0}</p>
              <p className="text-xs text-muted-foreground">{t('referrals.activeReferrals')}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Award className="h-4 w-4 text-yellow-600 mr-1" />
              </div>
              <p className="text-2xl font-bold text-yellow-600">{stats?.total_credits_earned || 0}</p>
              <p className="text-xs text-muted-foreground">{t('referrals.creditsEarned')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {recentReferrals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('referrals.recentReferrals')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentReferrals.map((referral: any) => (
                <div key={referral.user_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    {referral.avatar_url ? (
                      <img 
                        src={referral.avatar_url} 
                        alt={referral.full_name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{referral.full_name || 'New User'}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {referral.referral_completed && (
                    <div className="h-2 w-2 rounded-full bg-green-500" title="Active" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share Modal */}
      <GenericShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={t('referrals.shareTitle', 'Join Petius')}
        description={t('referrals.shareDescription', 'Invite friends to join Petius and earn rewards!')}
        shareText={shareText}
        shareUrl={shareUrl}
      />
    </div>
  );
};
